/**
 * HelpTooltip
 * --------------------------------------------------------------------------
 * A small "?" icon shown next to non-obvious form fields. Click reveals a
 * short plain-English explanation in a popover, plus optionally:
 *   - a "Learn more →" link that deep-links to the matching help article
 *   - a "Show me an example" button that pre-fills the field with a sample
 *     value so new users see the correct format at a glance.
 *
 * Usage:
 *   <HelpTooltip
 *     label="Severity"
 *     body="How bad the outcome could be, on a scale of 1 to 5."
 *     articleId="task-file-incident"
 *     example="major"
 *     onApplyExample={(val) => setFormData({...formData, severity: val})}
 *   />
 */
import React, { useState } from 'react';
import { HelpCircle, Wand2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';

export default function HelpTooltip({
  label,
  body,
  articleId,
  side = 'right',
  example,          // string | number — value to pre-fill
  exampleLabel,     // optional custom label for the example chip
  onApplyExample,   // (value) => void — parent hooks the value into their state
}) {
  const [open, setOpen] = useState(false);

  const goToArticle = (e) => {
    e.preventDefault();
    if (articleId) {
      try {
        sessionStorage.setItem('aeroguard_help_target_article', articleId);
      } catch {
        /* sessionStorage may be disabled — silently ignore */
      }
    }
    window.location.href = '/help-center';
  };

  const applyExample = (e) => {
    e.preventDefault();
    if (typeof onApplyExample === 'function') {
      onApplyExample(example);
      toast.success(`Example applied to ${label || 'field'}`);
      setOpen(false);
    }
  };

  const canApply = example !== undefined && example !== null && example !== '' && typeof onApplyExample === 'function';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Help: ${label || 'this field'}`}
          data-testid={`help-tooltip-${(articleId || label || 'field').toLowerCase().replace(/\s+/g, '-')}`}
          className="inline-flex items-center justify-center w-4 h-4 ml-1 text-slate-400 hover:text-sky-400 transition-colors cursor-help align-middle"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align="start"
        className="w-72 bg-slate-900 border-slate-700 text-slate-200 text-xs leading-relaxed p-3 shadow-xl"
      >
        {label && (
          <div className="font-semibold text-sky-300 mb-1 text-[13px]">{label}</div>
        )}
        <div className="whitespace-pre-line">{body}</div>
        {canApply && (
          <div className="mt-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 p-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">
              Sample value
            </div>
            <div className="flex items-center justify-between gap-2">
              <code className="text-[11px] text-emerald-100 font-mono truncate">
                {String(example)}
              </code>
              <button
                type="button"
                onClick={applyExample}
                data-testid={`help-tooltip-apply-example-${(articleId || label || 'field').toLowerCase().replace(/\s+/g, '-')}`}
                className="shrink-0 inline-flex items-center gap-1 text-[10px] text-emerald-200 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded px-2 py-1"
              >
                <Wand2 className="w-3 h-3" />
                {exampleLabel || 'Show me an example'}
              </button>
            </div>
          </div>
        )}
        {articleId && (
          <button
            type="button"
            onClick={goToArticle}
            className="mt-2 inline-flex text-[11px] text-violet-300 hover:text-violet-200 underline underline-offset-2"
          >
            Learn more →
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
